import { chunk } from 'lodash-es';
import { differenceInMilliseconds } from 'date-fns';
import { parseZonedDateTime } from '@internationalized/date';
import { BaseLocationNode, RouteChildNodeDataType, RouteNodeType } from '@lp/features/RoutePlanner/types';
import {
  LegLocationTypeEnum,
  RouteLocationType,
  AirportRouteLocation,
  HubRouteLocation,
  HospitalRouteLocation,
  TxCenterRouteLocation,
  SegmentTypeEnum,
  AirportLegLocationReference,
  FacilityLegLocationReference,
  CustomAddressLegLocationReference,
  AviaLegTypes,
  GroundLegTypes,
} from '@/api/apis/route';
import { RouteSegmentType } from '@/types/route';
import { OrganType } from '@/api/apis/case';
import { getTzDateTimeString, pickLocationDisplayName, WithExtendedLocation } from '@/utils/helpers';
import { getSegmentMapKey } from '@/utils/route';

export const getNodeTitle = (node: BaseLocationNode, schemaType: 'main' | 'alt'): string => {
  const isAltSchema = schemaType === 'alt';

  switch (node.locationType) {
    case LegLocationTypeEnum.airport: {
      const airportData = node.locationData as WithExtendedLocation<AirportRouteLocation>;

      return airportData?.iata || airportData?.icao || '';
    }
    case LegLocationTypeEnum.hub: {
      const hub = node.locationData as WithExtendedLocation<HubRouteLocation>;

      return hub?.code || 'HUB';
    }
    case LegLocationTypeEnum.hospital: {
      const hospital = node.locationData as WithExtendedLocation<HospitalRouteLocation>;

      return isAltSchema ? 'DH' : pickLocationDisplayName(hospital);
    }
    case LegLocationTypeEnum.txcenter: {
      const txCenter = node.locationData as WithExtendedLocation<TxCenterRouteLocation>;

      return isAltSchema ? 'TXC' : pickLocationDisplayName(txCenter);
    }
    case LegLocationTypeEnum.homeResidence:
    case LegLocationTypeEnum.custom: {
      const locationData = node.locationData as WithExtendedLocation<RouteLocationType>;

      return pickLocationDisplayName(locationData);
    }
    default:
      return '';
  }
};

export const getBaseParentNode = (
  node: BaseLocationNode,
  organType: OrganType,
  schemaType: 'main' | 'alt',
): RouteNodeType => {
  return {
    id: node.id,
    type: node.locationType,
    position: { x: 0, y: 0 },
    width: 0,
    data: {
      ...node,
      title: getNodeTitle(node, schemaType),
      organType,
      schemaType,
      nodeType: 'parent' as const,
      childrenTop: 0,
      childrenBottom: 0,
    },
  };
};

export const getBaseChildNode = (
  node: BaseLocationNode,
  parentId: string,
  connectionType: 'from' | 'to',
  organType: OrganType,
  schemaType: 'main' | 'alt',
): RouteNodeType<RouteChildNodeDataType> => {
  return {
    id: node.id,
    type: node.locationType,
    position: { x: 0, y: 0 },
    width: 0,
    data: {
      ...node,
      title: getNodeTitle(node, schemaType),
      organType,
      schemaType,
      nodeType: 'child' as const,
      parentId,
      connectionType,
    },
  };
};

const FACILITY_LOCATION_TYPES = [LegLocationTypeEnum.hospital, LegLocationTypeEnum.txcenter];

// Route may contain several DH/TXC nodes, so the parent is matched by location id, not only by type
const findFacilityParentNodes = (
  location: RouteSegmentType['startLocation'],
  parentNodes: BaseLocationNode[],
): BaseLocationNode[] => {
  if (!FACILITY_LOCATION_TYPES.includes(location.locationType) || !location.locationId) return [];

  return parentNodes.filter(
    (node) =>
      node.locationType === location.locationType && (node.locationId ?? node.locationData?.id) === location.locationId,
  );
};

const findParentNodeByStartLocationTime = (
  segment: RouteSegmentType,
  parentNodes: BaseLocationNode[],
): BaseLocationNode | null => {
  const { startLocation, startTime } = segment;
  const facilityParentNodes = findFacilityParentNodes(startLocation, parentNodes);

  if (facilityParentNodes.length === 1) return facilityParentNodes[0];

  if (!startTime) return facilityParentNodes.at(0) || null;

  const startTimeUTC = parseZonedDateTime(getTzDateTimeString(startTime)).toAbsoluteString();
  const searchNodes = facilityParentNodes.length ? facilityParentNodes : parentNodes;

  const parentNode = searchNodes.findLast((node) => {
    if (!node.startTime || !startLocation.locationId) return false;

    const isNodeEndTimeBefore = differenceInMilliseconds(new Date(startTimeUTC), new Date(node.startTime)) >= 0;
    const hasLocation = node.includeLocationIds.includes(startLocation.locationId);

    return isNodeEndTimeBefore && hasLocation;
  });

  return parentNode || facilityParentNodes.at(0) || null;
};

const findParentNodeByEndLocationTime = (
  segment: RouteSegmentType,
  parentNodes: BaseLocationNode[],
): BaseLocationNode | null => {
  const { endLocation, endTime } = segment;
  const facilityParentNodes = findFacilityParentNodes(endLocation, parentNodes);

  if (facilityParentNodes.length === 1) return facilityParentNodes[0];

  if (!endTime) return facilityParentNodes.at(0) || null;

  const endTimeUTC = parseZonedDateTime(getTzDateTimeString(endTime)).toAbsoluteString();
  const searchNodes = facilityParentNodes.length ? facilityParentNodes : parentNodes;

  const parentNode = searchNodes.find((node) => {
    if (!node.endTime || !endLocation.locationId) return false;

    const isNodeEndTimeBefore = differenceInMilliseconds(new Date(node.endTime), new Date(endTimeUTC)) >= 0;
    const hasLocation = node.includeLocationIds.includes(endLocation.locationId);

    return isNodeEndTimeBefore && hasLocation;
  });

  return parentNode || facilityParentNodes.at(0) || null;
};

export const getParentNodeId = (
  segment: RouteSegmentType,
  parentNodes: BaseLocationNode[],
): { parentId: string; connectionType: 'from' | 'to' } | null => {
  const isReposition = segment.type === SegmentTypeEnum.reposition;
  const isReturnToBase = segment.type === SegmentTypeEnum.returnToBase;
  const fromParent = findParentNodeByStartLocationTime(segment, parentNodes);
  const toParent = findParentNodeByEndLocationTime(segment, parentNodes);

  if (fromParent && !isReposition) {
    return {
      parentId: fromParent.id,
      connectionType: 'from',
    };
  }

  if (toParent && !isReturnToBase) {
    return {
      parentId: toParent.id,
      connectionType: 'to',
    };
  }

  return null;
};

const getSegmentNodeLocations = (
  segment: RouteSegmentType,
  segmentNode: AirportLegLocationReference | FacilityLegLocationReference | CustomAddressLegLocationReference,
  legs: (AviaLegTypes | GroundLegTypes)[],
): string[] => {
  switch (segmentNode.locationType) {
    case LegLocationTypeEnum.airport:
      return [segmentNode.locationId];
    case LegLocationTypeEnum.custom:
    case LegLocationTypeEnum.homeResidence:
    case LegLocationTypeEnum.hub:
      if (![SegmentTypeEnum.hospital, SegmentTypeEnum.txcenter, SegmentTypeEnum.hub].includes(segment.type)) {
        return [segmentNode.locationId];
      }

      const isStartLocation = segment.startLocation.locationId === segmentNode.locationId;
      const isEndLocation = segment.endLocation.locationId === segmentNode.locationId;

      return legs.reduce((locationList, i, currentIndex) => {
        const defaultLocationsList = [i.departureLocation.locationId, i.arrivalLocation.locationId];
        const isFirstLeg = !currentIndex;
        const isLastLeg = legs.length === currentIndex + 1;

        if (isFirstLeg) {
          const firstLegLocations = isStartLocation ? defaultLocationsList : [i.arrivalLocation.locationId];
          const locationIdsSet = new Set([...locationList, ...firstLegLocations]);
          return Array.from(locationIdsSet);
        }

        if (isLastLeg) {
          const lastLegLocations = isEndLocation ? defaultLocationsList : [i.departureLocation.locationId];
          const locationIdsSet = new Set([...locationList, ...lastLegLocations]);
          return Array.from(locationIdsSet);
        }

        const locationIdsSet = new Set([...locationList, ...defaultLocationsList]);
        return Array.from(locationIdsSet);
      }, [] as string[]);
    case LegLocationTypeEnum.hospital:
    case LegLocationTypeEnum.txcenter:
    default:
      return legs.reduce((locationList, i, currentIndex) => {
        const defaultLocationsList = [i.departureLocation.locationId, i.arrivalLocation.locationId];
        const isFirstLeg = !currentIndex;
        const isLastLeg = legs.length === currentIndex + 1;

        if (isFirstLeg) {
          const isArrivalToHub = i.arrivalLocation.locationType === LegLocationTypeEnum.hub;
          const locationId = isArrivalToHub ? i.departureLocation.locationId : i.arrivalLocation.locationId;
          const locationIdsSet = new Set([...locationList, locationId]);
          return Array.from(locationIdsSet);
        }

        if (isLastLeg) {
          const isSameArrival = i.arrivalLocation.locationId === segmentNode.locationId;
          const lastLegLocations = isSameArrival ? defaultLocationsList : [i.departureLocation.locationId];
          const locationIdsSet = new Set([...locationList, ...lastLegLocations]);
          return Array.from(locationIdsSet);
        }

        const locationIdsSet = new Set([...locationList, ...defaultLocationsList]);
        return Array.from(locationIdsSet);
      }, [] as string[]);
  }
};

const getBaseNodeTimestamps = (
  segment: RouteSegmentType,
  segmentNode: AirportLegLocationReference | FacilityLegLocationReference | CustomAddressLegLocationReference,
  legs: (AviaLegTypes | GroundLegTypes)[],
): Pick<BaseLocationNode, 'startTime' | 'endTime'> => {
  const isStartLocation = segment.startLocation.locationId === segmentNode.locationId;
  const isEndLocation = segment.startLocation.locationId === segmentNode.locationId;
  const isAirportNode = segmentNode.locationType === LegLocationTypeEnum.airport;
  const firstLeg = legs.at(0);
  const lastLeg = legs.at(-1);

  if (isAirportNode) {
    return {
      startTime: isStartLocation ? firstLeg?.departureTime : lastLeg?.arrivalTime,
      endTime: isEndLocation ? lastLeg?.departureTime : firstLeg?.arrivalTime,
    };
  }

  return {
    startTime: isStartLocation ? firstLeg?.departureTime : firstLeg?.arrivalTime,
    endTime: isEndLocation ? lastLeg?.arrivalTime : lastLeg?.departureTime,
  };
};

export const getBaseSegmentNode = (
  segment: RouteSegmentType,
  segmentNode: AirportLegLocationReference | FacilityLegLocationReference | CustomAddressLegLocationReference,
  legs: (AviaLegTypes | GroundLegTypes)[],
  locations: WithExtendedLocation<RouteLocationType>[],
): BaseLocationNode => {
  const includeLocationIds = getSegmentNodeLocations(segment, segmentNode, legs);

  return {
    id: `${segment.id}|${segmentNode.locationId || segmentNode.locationType}`,
    segmentId: segment.id,
    segmentType: segment.type,
    locationType: segmentNode.locationType,
    locationId: segmentNode.locationId,
    locationData: locations.find((l) => l.id === segmentNode.locationId) || null,
    includeLocationIds,
    ...getBaseNodeTimestamps(segment, segmentNode, legs),
  };
};

const splitSegmentLegs = (legs: (AviaLegTypes | GroundLegTypes)[], locationType: LegLocationTypeEnum) => {
  if (legs.length <= 1) {
    return [legs, legs];
  }

  if (locationType === LegLocationTypeEnum.airport) {
    const firstLegs = legs.at(0) as AviaLegTypes | GroundLegTypes;

    return [[firstLegs], legs];
  }

  const isEvenLegsNumber = legs.length % 2 === 0;
  const halfLegs = Math.ceil(legs.length / 2);
  const legsDivider = isEvenLegsNumber ? halfLegs + 1 : halfLegs;
  const [leftSide = [], rightSide = []] = chunk(legs, legsDivider);
  const rightLegs = [...(leftSide.length ? [leftSide.at(-1) as AviaLegTypes | GroundLegTypes] : []), ...rightSide];

  return [leftSide, rightLegs];
};

const getNodesFromSegmentLegs = (
  segment: RouteSegmentType,
  legs: (AviaLegTypes | GroundLegTypes)[],
  locations: WithExtendedLocation<RouteLocationType>[],
): { startNode: BaseLocationNode; endNode: BaseLocationNode } => {
  const [leftLegs, rightLegs] = splitSegmentLegs(legs, segment.startLocation.locationType);

  return {
    startNode: getBaseSegmentNode(segment, segment.startLocation, leftLegs, locations),
    endNode: getBaseSegmentNode(segment, segment.endLocation, rightLegs, locations),
  };
};

const mergeSameNodes = (newNode: BaseLocationNode, prevNode?: BaseLocationNode) => {
  if (!prevNode) return newNode;

  const mergedLocationIds = Array.from(new Set([...prevNode.includeLocationIds, ...newNode.includeLocationIds]));
  return {
    ...prevNode,
    startTime: prevNode.startTime ?? newNode.startTime,
    endTime: newNode.endTime ?? prevNode.endTime,
    includeLocationIds: mergedLocationIds,
  };
};

type GetBaseLocationNodesParams = {
  segments: RouteSegmentType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  locations: WithExtendedLocation<RouteLocationType>[];
};

export const getBaseLocationNodes = ({ segments, legs, locations }: GetBaseLocationNodesParams): BaseLocationNode[] => {
  const nodes: BaseLocationNode[] = [];

  segments.forEach((s) => {
    const segmentLegs = legs.get(getSegmentMapKey(s)) || [];

    const { startNode, endNode } = getNodesFromSegmentLegs(s, segmentLegs, locations);

    const prevNode = nodes.at(-1);
    const isSamePrevLocation = prevNode?.locationData?.id === startNode.locationData?.id;
    const hasAssignedLocation = startNode.locationData?.id;

    if (isSamePrevLocation && hasAssignedLocation) {
      nodes[nodes.length - 1] = mergeSameNodes(startNode, prevNode);
      nodes.push(endNode);
      return;
    }

    nodes.push(startNode, endNode);
  });

  return nodes;
};
